import {
    Card,
    CardContent,
    Stack,
    Typography,
    Chip,
    Divider,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    Button,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router';
import SlideUpTransition from '../components/SlideUpTransition';
import { Topic } from '../database/forum';
import {
    ChatBubbleOutline,
    Schedule,
    Delete,
    Close,
    IosShare,
    WarningAmber,
} from '@mui/icons-material';
import UserHeader from '../components/UserHeader';
import { useTheme } from '@mui/material';
import { useAuth } from '../auth/Auth';
import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { RequestSnackbar, useRequest } from '../api/Request';
import { useApi } from '../api/Api';

interface TopicCardProps {
    topic: Topic;
    forumId: string;
    forumName: string;
    forumSk: string;
    onDelete: (topic: Topic) => void;
    highlighted?: boolean;
}

const TopicCard: React.FC<TopicCardProps> = ({
    topic,
    forumId,
    forumName,
    forumSk,
    onDelete,
    highlighted = false,
}) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const auth = useAuth();
    const user = auth.user;
    const api = useApi();
    const deleteTopicRequest = useRequest();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showCopiedSnackbar, setShowCopiedSnackbar] = useState(false);

    const handleShare = async (e: React.MouseEvent) => {
        e.stopPropagation();
        const url = new URL(window.location.origin + '/ess');
        url.searchParams.set('topic', `${topic.pk}~${topic.sk}`);
        const shareUrl = url.toString();

        if (navigator.share) {
            try {
                await navigator.share({
                    title: topic.title,
                    url: shareUrl,
                });
                return;
            } catch (err) {
                if ((err as DOMException).name === 'AbortError') return;
            }
        }

        try {
            await navigator.clipboard.writeText(shareUrl);
            setShowCopiedSnackbar(true);
        } catch {
            console.error('Failed to copy to clipboard');
        }
    };

    const handleDelete = (topic: Topic) => {
        deleteTopicRequest.onStart();
        api.deleteTopic(forumSk, topic.sk)
            .then((response) => {
                onDelete(response.data);
                deleteTopicRequest.onSuccess('Successfully deleted topic');
            })
            .catch((err) => {
                console.error('Delete topic: ', err);
                deleteTopicRequest.onFailure(err);
            });
    };

    const formatTimeAgo = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (seconds < 60) return 'just now';
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
        if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
        return date.toLocaleDateString();
    };

    const topicNameAndSk = `${encodeURIComponent(topic.title)}-${encodeURIComponent(
        topic.sk,
    )}`;
    const forumNameAndSk = `${encodeURIComponent(forumName)}-${encodeURIComponent(
        forumSk,
    )}`;

    return (
        <>
            <Snackbar
                open={showCopiedSnackbar}
                autoHideDuration={2000}
                onClose={() => setShowCopiedSnackbar(false)}
                message='Link copied to clipboard'
            />
            <Dialog
                open={showDeleteDialog}
                onClose={
                    deleteTopicRequest.isLoading()
                        ? undefined
                        : () => setShowDeleteDialog(false)
                }
                TransitionComponent={SlideUpTransition}
                PaperProps={{
                    sx: {
                        borderColor: 'rgba(211, 47, 47, 0.4)',
                        boxShadow:
                            '0 24px 64px rgba(0,0,0,0.85), 0 0 0 1px rgba(211,47,47,0.2)',
                    },
                }}
            >
                <RequestSnackbar request={deleteTopicRequest} />
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmber sx={{ color: 'error.main', fontSize: '1.3rem' }} />
                    Delete this topic?
                    <IconButton
                        aria-label='close'
                        onClick={() => setShowDeleteDialog(false)}
                        sx={{ position: 'absolute', right: 10, top: 8 }}
                        disabled={deleteTopicRequest.isLoading()}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: theme.palette.secondary.main }}>
                        Are you sure you want to delete this topic and all its threads?
                        You can't undo this action.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        color='secondary'
                        onClick={() => setShowDeleteDialog(false)}
                        disabled={deleteTopicRequest.isLoading()}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        color='error'
                        variant='contained'
                        onClick={() => handleDelete(topic)}
                        loading={deleteTopicRequest.isLoading()}
                    >
                        Delete Topic
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            <motion.div
                initial={{ opacity: 0, x: -14 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                whileHover={{ x: 4 }}
                whileTap={{ scale: 0.985 }}
                transition={{ duration: 0.28, ease: 'easeOut' }}
                style={{ cursor: 'pointer', marginBottom: '16px' }}
                onClick={() => {
                    navigate(`/ess/${forumNameAndSk}/topic/${topicNameAndSk}`);
                }}
            >
                <Card
                    elevation={2}
                    sx={{
                        transition: 'box-shadow 0.25s ease, border-left-width 0.2s ease',
                        borderLeft: `4px solid ${theme.palette.primary.main}`,
                        ...(highlighted && {
                            boxShadow: `0 0 18px 6px ${theme.palette.primary.main}60`,
                            borderLeftWidth: '6px',
                        }),
                        '&:hover': {
                            boxShadow: `0px 4px 22px ${theme.palette.primary.main}50`,
                            borderLeftWidth: '6px',
                        },
                    }}
                >
                    <CardContent sx={{ py: 2 }}>
                        <Stack spacing={2}>
                            <Stack
                                direction='row'
                                spacing={2}
                                alignItems='flex-start'
                                justifyContent='space-between'
                            >
                                <Typography
                                    variant='h6'
                                    sx={{
                                        flexGrow: 1,
                                        fontFamily: 'LemonMilk',
                                        fontSize: '1.1rem',
                                    }}
                                >
                                    {topic.title}
                                </Typography>
                                <Stack direction='row' spacing={1} alignItems='center'>
                                    <Chip
                                        icon={
                                            <ChatBubbleOutline
                                                sx={{ fontSize: '1rem' }}
                                            />
                                        }
                                        label={
                                            topic.threadCount === 1
                                                ? '1 reply'
                                                : `${topic.threadCount} replies`
                                        }
                                        size='small'
                                        color='primary'
                                        variant='outlined'
                                        sx={{
                                            minWidth: '60px',
                                            fontWeight: 'bold',
                                        }}
                                    />
                                    {user && (user.isAdmin || user.isArtist) && (
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowDeleteDialog(true);
                                            }}
                                            sx={{
                                                color: `${theme.palette.primary.main}`,
                                            }}
                                        >
                                            <Delete />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        onClick={handleShare}
                                        sx={{
                                            color: `${theme.palette.primary.main}`,
                                        }}
                                    >
                                        <IosShare fontSize='small' />
                                    </IconButton>
                                </Stack>
                            </Stack>

                            <Divider sx={{ opacity: 0.3 }} />

                            <Stack
                                direction='row'
                                spacing={2}
                                alignItems='center'
                                justifyContent='space-between'
                            >
                                <UserHeader
                                    username={topic.username}
                                    displayName={topic.displayName}
                                    isArtist={topic.isArtist}
                                    subscriptionStatus={topic.subscriptionStatus}
                                    subscriptionOverride={topic.subscriptionOverride}
                                    timestamp={topic.createdAt}
                                    avatarSize={32}
                                    showTime={false}
                                />
                                <Stack direction='row' spacing={0.5} alignItems='center'>
                                    <Schedule sx={{ fontSize: '0.9rem', opacity: 0.7 }} />
                                    <Typography
                                        variant='caption'
                                        sx={{
                                            opacity: 0.7,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Last reply {formatTimeAgo(topic.lastReplyAt)}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
            </motion.div>
        </>
    );
};

export default TopicCard;
